import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '@/hooks/redux';
import { RootState } from '@/store';
import { getEffectiveTimezone } from '@/utils/timezoneUtils';
import {
  Box,
  Card,
  CardContent,

  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  Fab,
  Skeleton,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Sync as SyncIcon,
  Schedule as ScheduleIcon,
  Groups as GroupsIcon,
  SportsSoccer as MatchIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';
import {
  GlobalLeague,
  GlobalLeagueDivision,
  globalLeagueAdminService,
} from '@/services/globalLeagueService';
import GlobalLeagueFormDialog from './GlobalLeagueFormDialog';
import { LeagueLogo } from '@/components/LeagueLogo';
import { useScrollFab } from '@/hooks/useScrollFab';
import { useGlobalCatalogStream, CacheStats, CatalogDiscoveryProgress } from '@/hooks/useGlobalCatalogStream';

dayjs.extend(utc);
dayjs.extend(tz);

// Convert HH:mm:ss UTC string to dayjs in user's timezone for display
const timeStringToDayjs = (timeStr: string, userTimezone: string): Dayjs | null => {
  if (!timeStr) return null;
  const [hours, minutes, seconds] = timeStr.split(':').map(Number);
  return dayjs.utc().hour(hours).minute(minutes).second(seconds || 0).tz(userTimezone);
};

// Convert dayjs in user's timezone back to HH:mm:ss UTC string for storage
const dayjsToTimeString = (time: Dayjs | null, userTimezone: string): string => {
  if (!time) return '06:00:00';
  const utcTime = time.tz(userTimezone, true).utc();
  return utcTime.format('HH:mm:ss');
};

const GlobalLeagueCatalogTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAppSelector((state: RootState) => state.auth);
  const { settings: orgSettings } = useAppSelector((state: RootState) => state.organization);
  const userTimezone = useMemo(() => getEffectiveTimezone(user, orgSettings), [user, orgSettings]);
  const [leagues, setLeagues] = useState<GlobalLeague[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fabRef = useScrollFab(100);

  // Use refs to avoid re-renders during typing
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const searchQueryRef = useRef('');

  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingLeague, setEditingLeague] = useState<GlobalLeague | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leagueToDelete, setLeagueToDelete] = useState<GlobalLeague | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Selected league for edit view (null = list view) - synced with URL
  const selectedLeagueId = searchParams.get('league');
  const [selectedLeague, setSelectedLeague] = useState<GlobalLeague | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingScheduleId, setSavingScheduleId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editLogoUrl, setEditLogoUrl] = useState('');
  const [editPlayoffsEnabled, setEditPlayoffsEnabled] = useState(false);
  const [editPlayoffSpots, setEditPlayoffSpots] = useState(4);
  const [editWinPoints, setEditWinPoints] = useState(3);
  const [editDrawPoints, setEditDrawPoints] = useState(1);
  const [editLossPoints, setEditLossPoints] = useState(0);
  const [downloadingLogo, setDownloadingLogo] = useState(false);

  // Track whether league-level fields have unsaved changes
  const hasLeagueChanges = useMemo(() => {
    if (!selectedLeague) return false;
    return (
      editName !== selectedLeague.name ||
      editLogoUrl !== (selectedLeague.logoUrl || '') ||
      editPlayoffsEnabled !== (selectedLeague.playoffsEnabled ?? false) ||
      editPlayoffSpots !== (selectedLeague.playoffSpots ?? 4) ||
      editWinPoints !== (selectedLeague.winPoints ?? 3) ||
      editDrawPoints !== (selectedLeague.drawPoints ?? 1) ||
      editLossPoints !== (selectedLeague.lossPoints ?? 0)
    );
  }, [selectedLeague, editName, editLogoUrl, editPlayoffsEnabled, editPlayoffSpots, editWinPoints, editDrawPoints, editLossPoints]);

  // Track expanded division cards (persists across refreshes)
  const [expandedDivisionIds, setExpandedDivisionIds] = useState<Set<string>>(new Set());

  // Unified SSE hook — replaces useGlobalSyncStream, useQuery('teamCacheStats'), and discovery SSE reconnection
  const { syncingSchedules, discoveryProgress, cacheStats } = useGlobalCatalogStream({
    onSyncComplete: (globalScheduleId, result) => {
      setSelectedLeague(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          divisions: prev.divisions?.map(div => {
            if (div.schedule?.id !== globalScheduleId) return div;
            return {
              ...div,
              schedule: div.schedule ? {
                ...div.schedule,
                lastSyncAt: new Date().toISOString(),
                lastSyncStatus: 'success',
                matchCount: (div.schedule.matchCount || 0) + (result?.matchesCreated || 0) - (result?.matchesDeleted || 0),
              } : div.schedule,
            };
          }),
        };
      });
    },
    onSyncError: (globalScheduleId, error) => {
      toast.error(error || 'Update failed');
      setSelectedLeague(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          divisions: prev.divisions?.map(div => {
            if (div.schedule?.id !== globalScheduleId) return div;
            return {
              ...div,
              schedule: div.schedule ? {
                ...div.schedule,
                lastSyncAt: new Date().toISOString(),
                lastSyncStatus: 'error',
              } : div.schedule,
            };
          }),
        };
      });
    },
    onDiscoveryDone: (leagueId: string) => {
      loadLeagues(searchQueryRef.current);
      // Retry team caching for divisions that may have empty team lists
      globalLeagueAdminService.retryTeamCache(leagueId).catch(() => {});
    },
    onDiscoveryError: () => {
      loadLeagues(searchQueryRef.current);
    },
  });

  const loadLeagues = useCallback(async (query?: string, isInitial = false) => {
    try {
      if (isInitial) {
        setInitialLoading(true);
      } else {
        setSearching(true);
      }
      setError(null);
      const data = await globalLeagueAdminService.listGlobalLeagues(query || undefined);
      setLeagues(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load leagues');
    } finally {
      setInitialLoading(false);
      setSearching(false);
    }
  }, []);

  // Load selected league for edit view
  const loadSelectedLeague = useCallback(async () => {
    if (!selectedLeagueId) return;

    try {
      setEditLoading(true);
      setError(null);
      const data = await globalLeagueAdminService.getGlobalLeagueWithSchedules(selectedLeagueId);
      setSelectedLeague(data);
      setEditName(data.name);
      setEditLogoUrl(data.logoUrl || '');
      setEditPlayoffsEnabled(data.playoffsEnabled ?? false);
      setEditPlayoffSpots(data.playoffSpots ?? 4);
      setEditWinPoints(data.winPoints ?? 3);
      setEditDrawPoints(data.drawPoints ?? 1);
      setEditLossPoints(data.lossPoints ?? 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load league');
    } finally {
      setEditLoading(false);
    }
  }, [selectedLeagueId]);

  // Initial load
  useEffect(() => {
    loadLeagues('', true);
  }, [loadLeagues]);

  // Load selected league when id changes
  useEffect(() => {
    if (selectedLeagueId) {
      loadSelectedLeague();
    }
  }, [selectedLeagueId, loadSelectedLeague]);

  // Handle search input with debounce (no state update during typing)
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    searchQueryRef.current = value;

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      loadLeagues(value);
    }, 300);
  }, [loadLeagues]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleAddClick = () => {
    setEditingLeague(null);
    setFormDialogOpen(true);
  };

  const handleEditClick = (league: GlobalLeague) => {
    // Show edit view within this tab - update URL with league param
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('league', league.id);
      return newParams;
    });
  };

  const handleBackToList = () => {
    // Remove league param from URL
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('league');
      return newParams;
    });
    setSelectedLeague(null);
    setEditName('');
    setEditLogoUrl('');
    setEditPlayoffsEnabled(false);
    setEditPlayoffSpots(4);
    setEditWinPoints(3);
    setEditDrawPoints(1);
    setEditLossPoints(0);
    // Refresh list in case changes were made
    loadLeagues(searchQueryRef.current);
  };

  const handleSaveLeague = async () => {
    if (!selectedLeagueId) return;

    // Skip sending logoUrl if it matches the already-downloaded source or local path
    const logoAlreadyDownloaded =
      editLogoUrl === selectedLeague?.logoSourceUrl ||
      editLogoUrl === selectedLeague?.logoUrl;
    try {
      setSaving(true);
      await globalLeagueAdminService.updateGlobalLeague(selectedLeagueId, {
        name: editName,
        logoUrl: logoAlreadyDownloaded ? undefined : (editLogoUrl || undefined),
        playoffsEnabled: editPlayoffsEnabled,
        playoffSpots: editPlayoffsEnabled ? editPlayoffSpots : undefined,
        winPoints: editWinPoints,
        drawPoints: editDrawPoints,
        lossPoints: editLossPoints,
      });
      // Update local state so the header reflects changes instantly
      setSelectedLeague(prev => prev ? {
        ...prev,
        name: editName,
        playoffsEnabled: editPlayoffsEnabled,
        playoffSpots: editPlayoffsEnabled ? editPlayoffSpots : prev.playoffSpots,
        winPoints: editWinPoints,
        drawPoints: editDrawPoints,
        lossPoints: editLossPoints,
      } : prev);
      toast.success('League settings updated and cascaded to all divisions');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update league');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadLogo = async () => {
    if (!selectedLeagueId || !editLogoUrl) return;

    try {
      setDownloadingLogo(true);
      const updated = await globalLeagueAdminService.downloadGlobalLeagueLogo(selectedLeagueId, editLogoUrl);
      setSelectedLeague(updated);
      setEditLogoUrl(updated.logoUrl || editLogoUrl);
      toast.success('Logo downloaded successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.message || 'Failed to download logo');
    } finally {
      setDownloadingLogo(false);
    }
  };

  const handleTriggerSync = async (scheduleId: string) => {
    try {
      // SSE hook will track syncing state and handle completion automatically
      const result = await globalLeagueAdminService.triggerGlobalSync(scheduleId);
      toast.info(`Update started (Job ID: ${result.jobId})`);
    } catch (err: any) {
      if (err.response?.status === 409) {
        toast.info('An update is already in progress. Please wait for it to complete.');
      } else {
        toast.error(err.message || 'Failed to start update');
      }
    }
  };

  const handleSaveSettings = async (
    scheduleId: string,
    divisionId: string,
    settings: {
      autoSyncEnabled: boolean;
      syncFrequency: 'hourly' | 'daily' | 'weekly';
      syncTimeOfDay: string;
      syncPriority: number;
      name?: string;
    }
  ) => {
    try {
      setSavingScheduleId(scheduleId);

      // Update schedule settings
      await globalLeagueAdminService.updateGlobalScheduleSettings(scheduleId, {
        autoSyncEnabled: settings.autoSyncEnabled,
        syncFrequency: settings.syncFrequency,
        syncTimeOfDay: settings.syncTimeOfDay,
        syncPriority: settings.syncPriority,
      });

      // Update division name if changed
      if (settings.name !== undefined) {
        await globalLeagueAdminService.updateGlobalLeagueDivision(divisionId, {
          name: settings.name,
        });
      }

      toast.success('Settings saved. Changes cascaded to all organizations.');

      // Update local state in place instead of reloading (preserves scroll position)
      setSelectedLeague(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          divisions: prev.divisions?.map(div => {
            if (div.id !== divisionId) return div;
            return {
              ...div,
              name: settings.name ?? div.name,
              schedule: div.schedule ? {
                ...div.schedule,
                autoSyncEnabled: settings.autoSyncEnabled,
                syncFrequency: settings.syncFrequency,
                syncTimeOfDay: settings.syncTimeOfDay,
                syncPriority: settings.syncPriority,
              } : div.schedule,
            };
          }),
        };
      });
    } catch (err: any) {
      toast.error(err.message || 'Failed to save settings');
      throw err;
    } finally {
      setSavingScheduleId(null);
    }
  };

  const handleFormSuccess = (_league: GlobalLeague) => {
    setFormDialogOpen(false);
    setEditingLeague(null);
    loadLeagues(searchQueryRef.current);
  };

  const handleDeleteClick = (league: GlobalLeague) => {
    setLeagueToDelete(league);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!leagueToDelete) return;

    setDeleting(true);
    try {
      await globalLeagueAdminService.deleteGlobalLeague(leagueToDelete.id);
      setDeleteDialogOpen(false);
      setLeagueToDelete(null);
      loadLeagues(searchQueryRef.current);
    } catch (err: any) {
      setError(err.message || 'Failed to delete league');
    } finally {
      setDeleting(false);
    }
  };

  const handleRefreshClick = async (league: GlobalLeague) => {
    // Fire-and-forget POST — progress/done/error come via the unified SSE stream
    await globalLeagueAdminService.refreshDivisionsWithProgress(league.id, {
      onProgress: () => {},
      onResult: () => {},
      onError: (errorMsg) => {
        setError(errorMsg);
      },
    });
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return `${Math.floor(diffMins / 1440)}d ago`;
  };

  const formatStatus = (status: string) => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'in_progress':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Edit view for selected league
  if (selectedLeagueId) {
    if (editLoading) {
      return (
        <Box sx={{ maxWidth: 1200 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        </Box>
      );
    }

    if (error || !selectedLeague) {
      return (
        <Box sx={{ maxWidth: 1200 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || 'League not found'}
          </Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBackToList}>
            Back to Catalog
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ maxWidth: 1200 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
          <IconButton onClick={handleBackToList} sx={{ display: { xs: 'none', sm: 'flex' } }}>
            <ArrowBackIcon />
          </IconButton>
          <LeagueLogo leagueName={selectedLeague.name} size={64} logoPath={selectedLeague.logoPath} logoUrl={selectedLeague.logoUrl} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4">{selectedLeague.name}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Chip
                label={selectedLeague.platformType === 'GOTSPORT' ? 'GotSport' : selectedLeague.platformType === 'MODULAR11' ? 'Modular11' : selectedLeague.platformType === 'TGS' ? 'TGS' : 'SincSports'}
                size="small"
                sx={
                  selectedLeague.platformType === 'MODULAR11' ? { bgcolor: 'grey.600', color: 'white' } :
                  selectedLeague.platformType === 'TGS' ? { bgcolor: '#7b1fa2', color: 'white' } :
                  undefined
                }
                color={selectedLeague.platformType === 'GOTSPORT' ? 'primary' : (selectedLeague.platformType === 'MODULAR11' || selectedLeague.platformType === 'TGS') ? undefined : 'info'}
              />
              <Chip
                label={`${selectedLeague.divisions?.length || 0} divisions`}
                size="small"
                variant="outlined"
              />
            </Box>
          </Box>
        </Box>

        {/* League Details */}
        <Typography variant="h6" gutterBottom>
          League Details
        </Typography>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <TextField
              fullWidth
              label="League Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Logo URL"
              value={editLogoUrl}
              onChange={(e) => setEditLogoUrl(e.target.value)}
              placeholder="https://example.com/logo.png"
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: editLogoUrl.startsWith('http') ? (
                  <InputAdornment position="end">
                    <Tooltip title="Download logo">
                      <span>
                        <IconButton
                          edge="end"
                          onClick={handleDownloadLogo}
                          disabled={downloadingLogo}
                          size="small"
                        >
                          {downloadingLogo ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DownloadIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: { xs: '100%', sm: 256 }, mb: 2 }}>
              <FormControlLabel
                sx={{ ml: '-4px' }}
                control={
                  <Switch
                    checked={editPlayoffsEnabled}
                    onChange={(e) => setEditPlayoffsEnabled(e.target.checked)}
                    size="small"
                  />
                }
                label="Playoffs"
              />
              <TextField
                size="small"
                type="number"
                label="Teams"
                value={editPlayoffSpots}
                onChange={(e) => setEditPlayoffSpots(Number(e.target.value))}
                disabled={!editPlayoffsEnabled}
                sx={{ width: { xs: 'calc((100% - 16px) / 3)', sm: 80 } }}
                inputProps={{ min: 2, max: 16 }}
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Results Configuration
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, width: { xs: '100%', sm: 256 } }}>
              <TextField
                size="small"
                type="number"
                label="Win"
                value={editWinPoints}
                onChange={(e) => setEditWinPoints(Math.max(0, Math.min(10, Number(e.target.value))))}
                inputProps={{ min: 0, max: 10 }}
                sx={{ width: { xs: 'auto', sm: 80 }, flex: { xs: 1, sm: 'none' } }}
              />
              <TextField
                size="small"
                type="number"
                label="Draw"
                value={editDrawPoints}
                onChange={(e) => setEditDrawPoints(Math.max(0, Math.min(10, Number(e.target.value))))}
                inputProps={{ min: 0, max: 10 }}
                sx={{ width: { xs: 'auto', sm: 80 }, flex: { xs: 1, sm: 'none' } }}
              />
              <TextField
                size="small"
                type="number"
                label="Loss"
                value={editLossPoints}
                onChange={(e) => setEditLossPoints(Math.max(0, Math.min(10, Number(e.target.value))))}
                inputProps={{ min: 0, max: 10 }}
                sx={{ width: { xs: 'auto', sm: 80 }, flex: { xs: 1, sm: 'none' } }}
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Source URL
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-all', mb: 2 }}>
              {selectedLeague.website}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Last Discovery
            </Typography>
            <Typography variant="body2">{formatDate(selectedLeague.lastDiscoveryAt)}</Typography>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveLeague}
                disabled={saving || !hasLeagueChanges}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Divider sx={{ my: 3 }} />

        {/* Imported Divisions */}
        <Typography variant="h6" gutterBottom>
          Imported Divisions
        </Typography>

        {(() => {
          // Only show divisions that have been imported by at least one org
          const importedDivisions = selectedLeague.divisions?.filter(
            (div) => div.schedule && (div.schedule.subscribedOrgsCount ?? 0) > 0
          ) || [];

          if (importedDivisions.length === 0) {
            return (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    No divisions have been imported yet
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Divisions will appear here once organizations import them from the catalog.
                  </Typography>
                </CardContent>
              </Card>
            );
          }

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {importedDivisions.map((division) => (
                <DivisionScheduleCard
                  key={division.id}
                  division={division}
                  isSyncing={syncingSchedules.has(division.schedule?.id || '')}
                  isSaving={savingScheduleId === division.schedule?.id}
                  expanded={expandedDivisionIds.has(division.id)}
                  onToggleExpanded={() => {
                    setExpandedDivisionIds(prev => {
                      const next = new Set(prev);
                      if (next.has(division.id)) {
                        next.delete(division.id);
                      } else {
                        next.add(division.id);
                      }
                      return next;
                    });
                  }}
                  onTriggerSync={handleTriggerSync}
                  onSaveSettings={handleSaveSettings}
                  formatDate={formatDate}
                  formatRelativeTime={formatRelativeTime}
                  userTimezone={userTimezone}
                />
              ))}
            </Box>
          );
        })()}
      </Box>
    );
  }

  // List view
  return (
    <Box sx={{ maxWidth: 1200 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>League Catalog</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        >
          Add League
        </Button>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name or URL..."
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searching ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {/* Cache Stats */}
      {cacheStats && cacheStats.overall.total > 0 && (
        <CacheStatsCard
          cacheStats={cacheStats}
          discoveryProgress={discoveryProgress}
          leagues={leagues}
        />
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {initialLoading && (
        <Grid container spacing={2} sx={{ '& .MuiSkeleton-root': { animationDelay: '0s !important' } }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Skeleton animation="wave" variant="circular" width={64} height={64} />
                  </Box>
                  <Skeleton animation="wave" variant="text" width="60%" height={32} sx={{ mx: 'auto', mb: 1 }} />
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', mb: 1.5 }}>
                    <Skeleton animation="wave" variant="rounded" width={70} height={24} />
                    <Skeleton animation="wave" variant="rounded" width={80} height={24} />
                  </Box>
                  <Skeleton animation="wave" variant="text" width="80%" sx={{ mx: 'auto', mb: 1 }} />
                  <Skeleton animation="wave" variant="text" width="50%" sx={{ mx: 'auto' }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty state */}
      {!initialLoading && leagues.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Leagues Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add leagues that users across all organizations can select from.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddClick}>
              Add Your First League
            </Button>
          </CardContent>
        </Card>
      )}

      {/* League cards */}
      <Grid container spacing={2}>
        {leagues.map((league) => {
          const leagueCache = cacheStats?.leagues.find((l) => l.id === league.id);
          return (
          <Grid item xs={12} md={6} key={league.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1 }}>
                {/* Logo centered at top */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                  {league.platformType === 'TGS' ? (
                    <>
                      <Box
                        component="img"
                        src="/static/default-logos/ecnl-badge.png"
                        alt="ECNL"
                        sx={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 1 }}
                        onError={(e: any) => { e.target.style.display = 'none'; }}
                      />
                      <Box
                        component="img"
                        src="/static/default-logos/ecnl-rl-badge.jpg"
                        alt="ECNL RL"
                        sx={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 1 }}
                        onError={(e: any) => { e.target.style.display = 'none'; }}
                      />
                    </>
                  ) : (
                    <LeagueLogo
                      leagueName={league.name}
                      size={64}
                      logoPath={league.logoPath}
                      logoUrl={league.logoUrl}
                    />
                  )}
                </Box>

                {/* League name centered below logo */}
                <Typography
                  variant="h6"
                  title={league.name}
                  align="center"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mb: 1,
                  }}
                >
                  {league.name}
                </Typography>

                {/* Platform and status chips centered */}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center', mb: 1.5 }}>
                  <Chip
                    label={league.platformType === 'GOTSPORT' ? 'GotSport' : league.platformType === 'MODULAR11' ? 'Modular11' : league.platformType === 'TGS' ? 'TGS' : 'SincSports'}
                    size="small"
                    sx={
                      league.platformType === 'MODULAR11' ? { bgcolor: 'grey.600', color: 'white' } :
                      league.platformType === 'TGS' ? { bgcolor: '#7b1fa2', color: 'white' } :
                      undefined
                    }
                    color={league.platformType === 'GOTSPORT' ? 'primary' : (league.platformType === 'MODULAR11' || league.platformType === 'TGS') ? undefined : 'info'}
                  />
                  <Chip
                    label={formatStatus(discoveryProgress.has(league.id) ? 'in_progress' : league.discoveryStatus)}
                    size="small"
                    color={getStatusColor(discoveryProgress.has(league.id) ? 'in_progress' : league.discoveryStatus) as any}
                  />
                  <Chip
                    label={`${league._count?.divisions || league.divisions?.length || 0} divisions`}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} noWrap>
                  {league.website}
                </Typography>

                <Typography variant="caption" color="text.secondary" display="block">
                  Last discovered: {formatDate(league.lastDiscoveryAt)}
                </Typography>

                {/* Per-league progress: always rendered to keep card height stable */}
                <Box sx={{ mt: 1.5 }}>
                  {(() => {
                    const leagueDiscovery = discoveryProgress.get(league.id);
                    if (leagueDiscovery) {
                      return (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                            <Typography variant="caption" color="text.secondary">
                              {leagueDiscovery.message}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={leagueDiscovery.percent || 0}
                            sx={{ height: 4, borderRadius: 2 }}
                          />
                          <Typography variant="caption" sx={{ mt: 0.25, display: 'block', visibility: 'hidden' }}>
                            &nbsp;
                          </Typography>
                        </>
                      );
                    }
                    if (league.discoveryStatus === 'in_progress') {
                      return (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                            Discovery in progress...
                          </Typography>
                          <LinearProgress
                            variant="indeterminate"
                            sx={{ height: 4, borderRadius: 2 }}
                          />
                          <Typography variant="caption" sx={{ mt: 0.25, display: 'block', visibility: 'hidden' }}>
                            &nbsp;
                          </Typography>
                        </>
                      );
                    }
                    return null;
                  })()}
                  {!discoveryProgress.has(league.id) && league.discoveryStatus !== 'in_progress' && leagueCache && leagueCache.total > 0 ? (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                        Team Discovery
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(leagueCache.cached / leagueCache.total) * 100}
                        sx={{ height: 4, borderRadius: 2 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                        {leagueCache.empty > 0
                          ? `${leagueCache.cached}/${leagueCache.total} divisions \u2022 ${leagueCache.empty} with no teams`
                          : leagueCache.cached === leagueCache.total
                            ? `${leagueCache.total} divisions \u2022 Complete`
                            : `${leagueCache.cached}/${leagueCache.total} divisions \u2022 ${leagueCache.total - leagueCache.cached} remaining`}
                      </Typography>
                    </>
                  ) : null}
                </Box>

                {league.discoveryError && !discoveryProgress.has(league.id) && (
                  <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                    <Typography variant="caption">{league.discoveryError}</Typography>
                  </Alert>
                )}

              </CardContent>

              <Box sx={{ px: 2, pb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between' }}>
                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      <RefreshIcon
                        sx={discoveryProgress.has(league.id) || league.discoveryStatus === 'in_progress' ? {
                          animation: 'spin 1s linear infinite',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                          },
                        } : undefined}
                      />
                    }
                    onClick={() => handleRefreshClick(league)}
                    disabled={discoveryProgress.has(league.id) || league.discoveryStatus === 'in_progress'}
                  >
                    Refresh
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditClick(league)}
                  >
                    Edit
                  </Button>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDeleteClick(league)}
                >
                  Delete
                </Button>
              </Box>
            </Card>
          </Grid>
          );
        })}
      </Grid>

      {/* Form Dialog */}
      <GlobalLeagueFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setEditingLeague(null);
        }}
        onSuccess={handleFormSuccess}
        editingLeague={editingLeague}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><DeleteIcon />Delete League</Box></DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{leagueToDelete?.name}"? This will also delete all{' '}
            {leagueToDelete?._count?.divisions || leagueToDelete?.divisions?.length || 0} divisions.
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile FAB */}
      <Fab
        ref={fabRef}
        color="primary"
        aria-label="add league"
        sx={{
          position: 'fixed',
          bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))',
          right: 16,
          zIndex: 999, // Below bottom nav (1000) so FAB ducks behind it
          display: { xs: 'flex', sm: 'none' },
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        onClick={handleAddClick}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

// ============== Cache Stats Card ==============

interface CacheStatsProps {
  cacheStats: CacheStats;
  discoveryProgress: Map<string, CatalogDiscoveryProgress>;
  leagues: GlobalLeague[];
}

const CacheStatsCard: React.FC<CacheStatsProps> = ({ cacheStats, discoveryProgress, leagues }) => {
  const { overall } = cacheStats;
  const cachePercent = overall.total > 0 ? (overall.cached / overall.total) * 100 : 0;
  const isCacheComplete = overall.cached === overall.total;

  // Check if any league is refreshing (from SSE or from server state)
  const activeDiscoveryEntry = discoveryProgress.size > 0
    ? Array.from(discoveryProgress.values())[0]
    : null;
  const isRefreshing = !!activeDiscoveryEntry || leagues.some(l => l.discoveryStatus === 'in_progress');
  const refreshingLeague = activeDiscoveryEntry
    ? leagues.find(l => l.id === activeDiscoveryEntry.leagueId)
    : leagues.find(l => l.discoveryStatus === 'in_progress');

  // When refreshing, show refresh progress; otherwise show cache progress
  if (isRefreshing) {
    const hasProgress = !!activeDiscoveryEntry?.percent;
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {activeDiscoveryEntry?.message || `Refreshing ${refreshingLeague?.name || 'league'}...`}
            </Typography>
            <Chip label="Refreshing" size="small" color="warning" variant="outlined" />
          </Box>

          <LinearProgress
            variant={hasProgress ? 'determinate' : 'indeterminate'}
            value={hasProgress ? activeDiscoveryEntry!.percent : undefined}
            sx={{ mb: 1, height: 6, borderRadius: 3 }}
          />

          <Typography variant="caption" color="text.secondary">
            {hasProgress ? `${activeDiscoveryEntry!.percent}%` : 'Discovering divisions...'}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ pb: '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Team Cache: {overall.cached}/{overall.total} divisions
          </Typography>
          {isCacheComplete ? (
            <Chip label="Complete" size="small" color="success" variant="outlined" />
          ) : (
            <Chip label="In progress" size="small" color="warning" variant="outlined" />
          )}
        </Box>

        <LinearProgress
          variant="determinate"
          value={cachePercent}
          sx={{ mb: 1, height: 6, borderRadius: 3 }}
        />

        <Typography variant="caption" color="text.secondary">
          {overall.empty > 0
            ? `${overall.empty} divisions with no teams`
            : isCacheComplete
              ? 'All divisions cached'
              : `${overall.total - overall.cached} remaining`}
        </Typography>
      </CardContent>
    </Card>
  );
};

// ============== Division Schedule Card ==============

interface DivisionScheduleCardProps {
  division: GlobalLeagueDivision;
  isSyncing: boolean;
  isSaving: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
  onTriggerSync: (scheduleId: string) => void;
  onSaveSettings: (scheduleId: string, divisionId: string, settings: {
    autoSyncEnabled: boolean;
    syncFrequency: 'hourly' | 'daily' | 'weekly';
    syncTimeOfDay: string;
    syncPriority: number;
    name?: string;
  }) => Promise<void>;
  formatDate: (dateStr?: string) => string;
  formatRelativeTime: (dateStr?: string) => string;
  userTimezone: string;
}

const DivisionScheduleCard: React.FC<DivisionScheduleCardProps> = ({
  division,
  isSyncing,
  isSaving,
  expanded,
  onToggleExpanded,
  onTriggerSync,
  onSaveSettings,
  formatDate,
  userTimezone,
  formatRelativeTime,
}) => {
  const schedule = division.schedule;

  // Ref for focusing the name input when expanding via click
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Local state for all editable fields
  const [editName, setEditName] = useState(division.name);
  const [editAutoSync, setEditAutoSync] = useState(schedule?.autoSyncEnabled ?? true);
  const [editSyncTime, setEditSyncTime] = useState(schedule?.syncTimeOfDay ?? '06:00:00');
  const [editSyncFrequency, setEditSyncFrequency] = useState<'hourly' | 'daily' | 'weekly'>(schedule?.syncFrequency ?? 'daily');
  const [editSyncPriority, setEditSyncPriority] = useState(schedule?.syncPriority ?? 1);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!schedule) return false;
    return (
      editName !== division.name ||
      editAutoSync !== schedule.autoSyncEnabled ||
      editSyncTime !== (schedule.syncTimeOfDay ?? '06:00:00') ||
      editSyncFrequency !== schedule.syncFrequency ||
      editSyncPriority !== (schedule.syncPriority ?? 1)
    );
  }, [
    division, schedule, editName,
    editAutoSync, editSyncTime, editSyncFrequency,
    editSyncPriority
  ]);

  // Handle save
  const handleSave = async () => {
    if (!schedule) return;
    await onSaveSettings(schedule.id, division.id, {
      autoSyncEnabled: editAutoSync,
      syncFrequency: editSyncFrequency,
      syncTimeOfDay: editSyncTime,
      syncPriority: editSyncPriority,
      name: editName !== division.name ? editName : undefined,
    });
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggleExpanded}
      disableGutters
      sx={{
        '&:before': { display: 'none' },
        boxShadow: 1,
      }}
    >
      <AccordionSummary
        sx={{
          '& .MuiAccordionSummary-content': {
            mr: 1,
            // Use consistent margin whether expanded or not to prevent content shift
            // Match the bottom padding of AccordionDetails (16px = 2)
            my: 2,
            '&.Mui-expanded': {
              my: 2,
            },
          },
          // Prevent grey selection highlight when clicking input
          '&.Mui-focusVisible': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <Box sx={{ width: '100%' }}>
          {/* Title row with expand/collapse icon */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: { xs: '100%', sm: 'calc(33.333% - 8px)' } }}>
              {expanded ? (
                <TextField
                  size="small"
                  fullWidth
                  inputRef={nameInputRef}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder={division.sourceName || division.name}
                  label="Division Name"
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  sx={{
                    '& input::placeholder': {
                      color: 'text.disabled',
                      opacity: 1,
                    },
                  }}
                />
              ) : (
                <TextField
                  size="small"
                  fullWidth
                  value={division.name}
                  label="Division Name"
                  InputProps={{ readOnly: true }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!expanded) {
                      onToggleExpanded();
                    }
                    setTimeout(() => nameInputRef.current?.focus(), 50);
                  }}
                  sx={{
                    '& input': {
                      cursor: 'pointer',
                    },
                  }}
                />
              )}
            </Box>
            <Box sx={{ ml: 'auto' }}>
              {expanded ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />}
            </Box>
          </Box>
          {/* Stats Row - under the title */}
          {schedule && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1, ml: '-2px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <MatchIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">{schedule.matchCount || 0} matches</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <GroupsIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">{schedule.teamCount || 0} teams</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ScheduleIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  Last: {formatRelativeTime(schedule.lastSyncAt)}
                </Typography>
              </Box>
              {schedule.subscribedOrgsCount !== undefined && (
                <Typography variant="body2" color="text.secondary">
                  {schedule.subscribedOrgsCount} org{schedule.subscribedOrgsCount !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
          )}
          {/* Update button - only show when expanded */}
          {schedule && expanded && (
            <Box sx={{ mt: 2, width: { xs: '100%', sm: 'calc(33.333% - 8px)' } }} onClick={(e) => e.stopPropagation()}>
              <Button
                variant="outlined"
                fullWidth
                  startIcon={
                    <SyncIcon
                      sx={{
                        transform: 'scaleX(-1)',
                        ...(isSyncing && {
                          animation: 'spin 1s linear infinite',
                          '@keyframes spin': {
                            '0%': { transform: 'scaleX(-1) rotate(0deg)' },
                            '100%': { transform: 'scaleX(-1) rotate(-360deg)' }
                          }
                        })
                      }}
                    />
                  }
                  onClick={() => onTriggerSync(schedule.id)}
                  disabled={isSyncing}
                  sx={{
                    height: 40,
                    justifyContent: 'flex-start',
                    ...(isSyncing && {
                      '& .MuiButton-startIcon': {
                        color: 'primary.main'
                      }
                    })
                  }}
                >
                  <Box
                    component="span"
                    sx={isSyncing ? {
                      display: 'inline-flex',
                      transform: 'translateY(1px)',
                      '& > span': {
                        animation: 'wave 1.2s ease-in-out infinite',
                        display: 'inline-block'
                      },
                      '& > span:nth-of-type(1)': { animationDelay: '0s' },
                      '& > span:nth-of-type(2)': { animationDelay: '0.05s' },
                      '& > span:nth-of-type(3)': { animationDelay: '0.1s' },
                      '& > span:nth-of-type(4)': { animationDelay: '0.15s' },
                      '& > span:nth-of-type(5)': { animationDelay: '0.2s' },
                      '& > span:nth-of-type(6)': { animationDelay: '0.25s' },
                      '& > span:nth-of-type(7)': { animationDelay: '0.3s' },
                      '& > span:nth-of-type(8)': { animationDelay: '0.35s' },
                      '& > span:nth-of-type(9)': { animationDelay: '0.4s' },
                      '& > span:nth-of-type(10)': { animationDelay: '0.45s' },
                      '& > span:nth-of-type(11)': { animationDelay: '0.5s' },
                      '@keyframes wave': {
                        '0%, 100%': { transform: 'translateY(0)' },
                        '50%': { transform: 'translateY(-2px)' }
                      }
                    } : {}}
                  >
                    {isSyncing ? (
                      <>
                        <span>U</span><span>p</span><span>d</span><span>a</span><span>t</span><span>i</span><span>n</span><span>g</span><span>.</span><span>.</span><span>.</span>
                      </>
                    ) : 'Update Schedule'}
                  </Box>
                </Button>
            </Box>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {schedule ? (
          <>
            {/* Settings */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Auto-update Toggle */}
              <FormControlLabel
                sx={{ ml: '-2px' }}
                control={
                  <Switch
                    checked={editAutoSync}
                    onChange={(e) => setEditAutoSync(e.target.checked)}
                    size="small"
                  />
                }
                label="Auto-update"
              />

              {/* Sync Schedule Options */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
                    <TimePicker
                      label="Update Time"
                      value={timeStringToDayjs(editSyncTime, userTimezone)}
                      onChange={(newTime) => {
                        const timeStr = dayjsToTimeString(newTime, userTimezone);
                        setEditSyncTime(timeStr);
                      }}
                      timezone={userTimezone}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Update Frequency</InputLabel>
                    <Select
                      value={editSyncFrequency}
                      label="Update Frequency"
                      onChange={(e) => setEditSyncFrequency(e.target.value as 'hourly' | 'daily' | 'weekly')}
                    >
                      <MenuItem value="hourly">Hourly</MenuItem>
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Update Priority</InputLabel>
                    <Select
                      value={editSyncPriority}
                      label="Update Priority"
                      onChange={(e) => setEditSyncPriority(Number(e.target.value))}
                    >
                      <MenuItem value={2}>High</MenuItem>
                      <MenuItem value={1}>Normal</MenuItem>
                      <MenuItem value={0}>Low</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {editAutoSync && schedule.nextSyncAt
                ? `Next update: ${formatDate(schedule.nextSyncAt)}`
                : 'Auto updates disabled'}
            </Typography>

            {schedule.lastSyncError && (
              <Alert severity="error" sx={{ mt: 2, py: 0 }}>
                <Typography variant="caption">{schedule.lastSyncError}</Typography>
              </Alert>
            )}

            {/* Save button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 2 }}>
              {hasUnsavedChanges && (
                <Typography variant="body2" color="info.main">
                  You have unsaved changes
                </Typography>
              )}
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={isSaving || !hasUnsavedChanges}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </>
        ) : (
          <Alert severity="info" sx={{ py: 0 }}>
            <Typography variant="caption">
              No schedule created yet. Import this division to create a schedule.
            </Typography>
          </Alert>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default GlobalLeagueCatalogTab;
